import { apiClient } from '@/services/apiClient'

export type ExportEntityType = 'product' | 'workflow' | 'persona' | 'asset'

export interface Finding {
  severity: 'info' | 'warning' | 'error'
  code: string
  message: string
  entity_type?: string | null
  entity_key?: string | null
}

export type TargetAction = 'create' | 'update' | 'skip'

export interface PersonaTargetChoice {
  action: TargetAction
  persona_id?: string | null
}

export interface PersonaCandidate {
  id: string
  name: string
  same_name: boolean
  current_version: number | null
}

export interface PersonaIdentityMatch {
  id: string
  name: string
  archived: boolean
}

export interface PersonaTargetRead {
  /** The archive persona's own name (its key may be `import:<uuid>`). */
  name: string
  default: PersonaTargetChoice
  chosen: PersonaTargetChoice
  allowed: TargetAction[]
  identity_match: PersonaIdentityMatch | null
  candidates: PersonaCandidate[]
}

export interface PlanItem {
  entity_type: string
  natural_key: string
  action: 'create' | 'update' | 'unchanged' | 'skip'
  /** Absent (not null) on every item that is not a persona. */
  persona_target?: PersonaTargetRead
}

export interface CredentialSetOption {
  id: string
  name: string
}

export interface UnresolvedCredentialSet {
  name: string
  binding_count: number
  options: CredentialSetOption[]
}

/** What the person chose on the import page; each part is sent only when non-empty. */
export interface ImportChoices {
  personaTargets?: Record<string, PersonaTargetChoice>
  credentialSetMap?: Record<string, string>
}

/** A failed request; `detail` is the parsed FastAPI `detail` (a string, or `{code, key}` on a 422). */
export class PortabilityRequestError extends Error {
  status: number
  detail: unknown

  constructor(message: string, status: number, detail: unknown) {
    super(message)
    this.name = 'PortabilityRequestError'
    this.status = status
    this.detail = detail
  }
}

async function requestError(res: Response, what: string): Promise<PortabilityRequestError> {
  let detail: unknown = null
  try {
    const body = await res.json()
    if (body && typeof body === 'object' && 'detail' in body) {
      detail = (body as { detail: unknown }).detail
    }
  } catch {
    // no readable body: the status alone is the message
  }
  return new PortabilityRequestError(`${what} failed (${res.status})`, res.status, detail)
}

export interface TargetOwner {
  id: string
  email: string
  matched_by: 'explicit' | 'email'
}

export interface ImportPreview {
  manifest: { owner_email: string; exported_at: string; source_label: string }
  target_owner: TargetOwner | null
  plan: PlanItem[]
  findings: Finding[]
  /** Absent on an older server; read it as empty. */
  credential_sets?: UnresolvedCredentialSet[]
}

export interface EntityResult {
  entity_type: string
  natural_key: string
  action: 'created' | 'updated' | 'unchanged' | 'skipped' | 'error'
  detail?: string | null
}

export interface ImportApplyResult {
  results: EntityResult[]
  findings: Finding[]
}

export interface ExportPreviewEntity {
  type: string
  natural_key: string
}

export interface ExportPreview {
  entities: ExportPreviewEntity[]
  findings: Finding[]
}

export async function exportArchive(entityTypes: ExportEntityType[]): Promise<Blob> {
  const res = await apiClient.fetch('/api/admin/portability/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entity_types: entityTypes }),
  })
  if (!res.ok) throw new Error(`export failed (${res.status})`)
  return res.blob()
}

export async function previewExport(entityTypes: ExportEntityType[]): Promise<ExportPreview> {
  const res = await apiClient.fetch('/api/admin/portability/export/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entity_types: entityTypes }),
  })
  if (!res.ok) throw new Error(`preview failed (${res.status})`)
  return res.json()
}

function importForm(file: File, targetOwnerId?: string, opts?: ImportChoices): FormData {
  const fd = new FormData()
  fd.append('file', file)
  if (targetOwnerId) fd.append('target_owner_id', targetOwnerId)
  if (opts?.personaTargets && Object.keys(opts.personaTargets).length > 0) {
    fd.append('persona_targets', JSON.stringify(opts.personaTargets))
  }
  if (opts?.credentialSetMap && Object.keys(opts.credentialSetMap).length > 0) {
    fd.append('credential_set_map', JSON.stringify(opts.credentialSetMap))
  }
  return fd
}

export async function previewImport(
  file: File,
  targetOwnerId?: string,
  opts?: ImportChoices,
): Promise<ImportPreview> {
  const res = await apiClient.fetch('/api/admin/portability/import/preview', {
    method: 'POST',
    body: importForm(file, targetOwnerId, opts),
  })
  if (!res.ok) throw await requestError(res, 'preview')
  return res.json()
}

export async function applyImport(
  file: File,
  targetOwnerId?: string,
  opts?: ImportChoices,
): Promise<ImportApplyResult> {
  const res = await apiClient.fetch('/api/admin/portability/import/apply', {
    method: 'POST',
    body: importForm(file, targetOwnerId, opts),
  })
  if (!res.ok) throw await requestError(res, 'apply')
  return res.json()
}
